import * as React from 'react';
import classNames from 'classnames';

import { ITableHeadCellMapping, ITableStore } from '../Table';
import Icon from '../Icon';
import { calculateMapping } from '../Table/utils';

export interface ITableRowProps {
    /** Row open status */
    isOpen?: boolean;
    /** className to append */
    className?: string;
    /**
     * Internal tableStore data, applied automatically by parent.
     * @ignore
     */
    tableStore?: ITableStore;
    /** Optional onClick handler. Should not be used with interactive elements within table cells */
    onClick?: () => void;
    /** Indent level */
    level?: number;
}

interface IState {
    isOpen: boolean;
}

/**
 * Use this within Table component to render a new content row.
 */
export default class TableRow extends React.Component<ITableRowProps, IState> {
    static displayName: string = 'TableRow';

    constructor (props) {
        super(props);
        this.state = {
            isOpen: props.isOpen || false,
        };
    }

    public render () {
        if (!this.props.tableStore || !this.props.children) {
            return null;
        }
        const { headers, headersMapping, tableWidth } = this.props.tableStore;

        const cleanChildren: React.ReactElement<any>[] = [];

        React.Children.map(this.props.children, (child) => {
            if (!child || typeof child === 'string' || typeof child === 'number' || typeof child === 'boolean') {
                return;
            }
            if (React.isValidElement(child)) {
                const extendedProps = {
                    tableStore: this.props.tableStore,
                };
                cleanChildren.push(React.cloneElement(child, extendedProps));
            }
        });

        if (tableWidth === 0) {
            // not initialized, render full table
            const rows = cleanChildren.map((child, index) => {
                const extendedProps = {
                    tableStore: this.props.tableStore,
                    width: headers[index].props.width,
                    maxWidth: headers[index].props.maxWidth,
                    minWidth: headers[index].props.minWidth,
                };
                return headers[index].props.hidden ? null : (
                    <td
                        key={index}
                        className={child.props && child.props.className ? child.props.className : undefined}
                    >
                        {React.cloneElement(child, extendedProps)}
                    </td>
                );
            });
            return (
                <tbody>
                    <tr className={classNames(this.props.className, {
                        level1: this.props.level === 1,
                        level2: this.props.level === 2,
                        level3: this.props.level === 3,
                        level4: this.props.level === 4,
                        level5: this.props.level === 5,
                        level6: this.props.level === 6,
                    })}>{rows}</tr>
                </tbody>
            );
        } else {
            const { visible, hidden } = calculateMapping(headersMapping, tableWidth);
            const visibleHeaders = visible;
            const hiddenHeaders = hidden;

            const mapper = (map: ITableHeadCellMapping) => {
                return {
                    header: map.header,
                    row: this.props.children ? cleanChildren[map.index] : null,
                };
            };

            const visibleChildrenContainer = visibleHeaders.map(mapper);
            const hiddenChildrenContainer = hiddenHeaders.map(mapper);

            const toggle = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.setState({ isOpen: !this.state.isOpen });
            };

            let useNext = false;
            const visibleFields = visibleChildrenContainer.map((container, index) => {
                const extendedProps = {
                    tableStore: this.props.tableStore,
                    width: container.header.props.width,
                    minWidth: container.header.props.minWidth,
                    maxWidth: container.header.props.maxWidth,
                };
                if (!container.row) {
                    return null;
                }
                if (hiddenChildrenContainer.length > 0 && (index === 0 || (index === 1 && useNext)) && !container.header.props.checkbox) {
                    return (
                        <td key={index} className={classNames('toggler', container.row.props.className)}>
                            <button onClick={toggle} className="row-icon">
                                <Icon
                                    kind={this.state.isOpen ? 'arrow1-up' : 'arrow1-down'}
                                    width={10}
                                    height={10}
                                />
                            </button>
                            {React.cloneElement(container.row, extendedProps)}
                        </td>
                    );
                }
                if (container.header.props.checkbox) {
                    useNext = true;
                }
                return (
                    <td key={index} className={container.row.props.className}>
                        {React.cloneElement(container.row, extendedProps)}
                    </td>
                );
            });
            const hiddenFields = hiddenChildrenContainer.map((container, index) => {
                return (
                    <div className="detail-row" key={index}>
                        {container.header.props.children && (
                            <div className="detail-title">{container.header}</div>
                        )}
                        <div className="detail-content">{container.row}</div>
                    </div>
                );
            });

            const rows = [
                (
                    <tr
                        onClick={() => {
                            if (this.props.onClick) {
                                this.props.onClick();
                            }
                        }}
                        key="1"
                        className={classNames(this.props.className, {
                            clickable: this.props.onClick,
                            level1: this.props.level === 1,
                            level2: this.props.level === 2,
                            level3: this.props.level === 3,
                            level4: this.props.level === 4,
                            level5: this.props.level === 5,
                            level6: this.props.level === 6,
                        })}
                    >
                        {visibleFields}
                    </tr>
                ),
            ];
            if (hiddenFields.length > 0 && this.state.isOpen) {
                rows.push((
                    <tr key="2" className="sub">
                        <td colSpan={visibleFields.length}>
                            {hiddenFields}
                        </td>
                    </tr>
                ));
            }
            return (
                <tbody>{rows}</tbody>
            );
        }
    }
}
